import type { RealtimePresentationState } from './types'

export function isInstantPrompt(prompt: string): boolean {
  return prompt.trim() === '视频中角色跟随拖拽轨迹运动'
}

export const DEFAULT_PRESENTATION_STATE: Readonly<RealtimePresentationState> = Object.freeze({
  instant: false,
  inputImageUrl: null,
  showInputImage: false,
  suppressLoading: false,
})

/** One event-driven presentation owner for managed and consumer-rendered output. */
export class InstantPresentation {
  private value = DEFAULT_PRESENTATION_STATE
  private matched = false
  private dragging = false
  private idle = false
  private timer: ReturnType<typeof setTimeout> | null = null
  private frame: number | null = null
  private restartTask: Promise<void> | null = null
  private image: HTMLImageElement | null = null

  constructor(private readonly options: {
    container?: HTMLElement
    fit?: 'contain' | 'cover'
    transitionMs?: number
    onChange?: (state: Readonly<RealtimePresentationState>) => void
    restart: () => Promise<void>
  }) {}

  get state(): Readonly<RealtimePresentationState> { return this.value }

  activate(inputImageUrl: string): void {
    if (this.value.instant && this.value.inputImageUrl === inputImageUrl) return
    this.reset()
    if (this.options.container) {
      const image = document.createElement('img')
      image.src = inputImageUrl
      image.alt = ''
      image.setAttribute('aria-hidden', 'true')
      image.setAttribute('data-xmax-instant-image', 'true')
      image.style.cssText = `position:absolute;inset:0;z-index:30;width:100%;height:100%;object-fit:${this.options.fit ?? 'contain'};object-position:center;pointer-events:none;opacity:0;transition:opacity ${this.options.transitionMs ?? 150}ms cubic-bezier(0.4,0,0.2,1);`
      this.options.container.appendChild(image)
      this.image = image
    }
    this.publish({ ...DEFAULT_PRESENTATION_STATE, instant: true, inputImageUrl })
  }

  onGate(matched: boolean): void {
    if (matched === this.matched) return
    const justMatched = matched && !this.matched
    this.matched = matched
    if (!this.value.instant) return
    if (justMatched && this.dragging) this.idle = false
    this.updateVisibility()
  }

  beginDrag(): void {
    this.cancelPendingRestart()
    if (!this.value.instant) return
    this.dragging = true
    this.idle = !this.matched
    this.publish({ ...this.value, suppressLoading: true, showInputImage: this.idle })
  }

  endDrag(): void {
    if (!this.value.instant || !this.dragging) return
    this.cancelPendingRestart()
    this.timer = setTimeout(() => {
      this.timer = null
      this.dragging = false
      this.idle = true
      this.updateVisibility()
      if (!this.value.instant || !this.idle) return
      // Preserve the platform's paint-before-restart timing. No polling or busy wait.
      this.frame = requestAnimationFrame(() => {
        this.frame = requestAnimationFrame(() => {
          this.frame = null
          if (this.restartTask || !this.value.instant) return
          const task = this.options.restart().catch(() => {
            // Session owns error reporting and disconnect; never reject a pointer handler.
          })
          this.restartTask = task
          void task.finally(() => {
            if (this.restartTask === task) this.restartTask = null
          })
        })
      })
    }, 750)
  }

  reset(): void {
    this.cancelPendingRestart()
    this.restartTask = null
    this.matched = false
    this.dragging = false
    this.idle = false
    this.image?.remove()
    this.image = null
    this.publish(DEFAULT_PRESENTATION_STATE)
  }

  private cancelPendingRestart(): void {
    if (this.timer !== null) clearTimeout(this.timer)
    if (this.frame !== null) cancelAnimationFrame(this.frame)
    this.timer = null
    this.frame = null
  }

  private updateVisibility(): void {
    this.publish({
      ...this.value,
      showInputImage: this.value.suppressLoading ? this.idle : this.matched,
    })
  }

  private publish(next: Readonly<RealtimePresentationState>): void {
    if (next.instant === this.value.instant
      && next.inputImageUrl === this.value.inputImageUrl
      && next.showInputImage === this.value.showInputImage
      && next.suppressLoading === this.value.suppressLoading) return
    this.value = Object.freeze(next)
    if (this.image) this.image.style.opacity = next.showInputImage ? '1' : '0'
    try { this.options.onChange?.(this.value) } catch {
      // Consumer rendering must not interrupt the generation lifecycle.
    }
  }
}
